import React, { useLayoutEffect, useState } from 'react';
import { gsap, Linear } from 'gsap';
import { useIsMounted } from '@hooks/useIsMounted';
import { useScrollController } from '@context/ScrollControllerProvider/useScrollController';
import ScrollMagic from 'scrollmagic';
import ReactHtmlParser from 'react-html-parser';
import { Box } from '@components/Box';
import { SpacingStack } from '@components/SpacingStack';
import { BubbleTextWrapper, BubbleWrapper, NotificationNumberWrapper, Number, NumberUpdate, TrackWrapper } from './TestSendTrack.styles';
import { onlineRsvpPageData } from '@data-ts/pages/online-rsvp';
import { LazyLoadWrapper } from '@components/LazyLoadWrapper';

export const TestSendTrack: React.FC<{}> = () => {
  const testSendTrackData = onlineRsvpPageData.testSendTrack;
  const columnsData = testSendTrackData.columns;
  const sectionId = 'RSVP_Notifications';
  const [isScrolledIntoView, setScrolledIntoView] = useState(false);

  const scrollController = useScrollController();
  const isMounted = useIsMounted();

  useLayoutEffect(() => {
    let scene: InstanceType<typeof ScrollMagic.Scene> | undefined;
    let sectionTimeline: ReturnType<typeof gsap.timeline>;

    if (scrollController && isMounted) {
      const initTimeline = () => {
        sectionTimeline = gsap.timeline({ paused: true, defaults: { ease: Linear.easeNone } });

        sectionTimeline
          .to('#number3', { opacity: 0, duration: 0, delay: 1.2 })
          .to('#number4', { opacity: 1, duration: 0 })
          .to('#number4', { opacity: 0, duration: 0, delay: 0.5 })
          .to('#number5', { opacity: 1, duration: 0 });
      };
      const startScrollAnimation = () => {
        scene = new ScrollMagic.Scene({
          triggerElement: '#RSVP_Notifications',
          triggerHook: 1,
          duration: '100%',
        })
          .addTo(scrollController)
          .on('progress', (event: ScrollMagic.SceneProgressEvent) => {
            sectionTimeline?.progress(event.progress);
          });
      };
      initTimeline();
      startScrollAnimation();
    }
    const clearScene = () => {
      if (sectionTimeline) {
        sectionTimeline.progress(0);
        sectionTimeline.kill();
      }
      if (scene) {
        scene.destroy(true);
      }
    };
    return clearScene;
  }, [scrollController, isMounted]);

  return (
    <TrackWrapper isScrolledIntoView={isScrolledIntoView} paddingY={[10, 10, 12]} paddingX={0} id={sectionId}>
      <LazyLoadWrapper
        beforeLoad={() => {
          setScrolledIntoView(true);
        }}
      >
        <div />
      </LazyLoadWrapper>
      <SpacingStack spacing={[8, 12, 13]}>
        <BubbleWrapper paddingX={[120]} paddingY={[12]} marginY={[200]}>
          <Number id="number3" style={{ opacity: 1 }}>
            <NotificationNumberWrapper color="mono7" tagName="span" variant="calloutHed40">
              {columnsData[4].prevNumber}
            </NotificationNumberWrapper>
          </Number>
          <Number id="number4" style={{ opacity: 0 }}>
            <NotificationNumberWrapper color="mono7" tagName="span" variant="calloutHed40">
              {columnsData[4].number}
            </NotificationNumberWrapper>
          </Number>
          <NumberUpdate id="number5" style={{ opacity: 0 }}>
            <NotificationNumberWrapper color="mono7" tagName="span" variant="calloutHed40">
              {columnsData[4].updateNumber}
            </NotificationNumberWrapper>
          </NumberUpdate>
          <Box style={{ maxWidth: '800px', margin: 'auto' }}>
            <BubbleTextWrapper
              alignment="center"
              eyebrow={columnsData[4].eyebrow}
              eyebrowColor="blue5"
              eyebrowVariant="sectionEyeBrow24"
              eyebrowTagName="h2"
              title={ReactHtmlParser(columnsData[4].title)}
              titleColor="blue6"
              titleVariant="hed96Break"
              titleTagName="h2"
              body={columnsData[4].content}
              bodyVariant="dek24"
              bodyColor="blue6"
            />
          </Box>
        </BubbleWrapper>
      </SpacingStack>
    </TrackWrapper>
  );
};
